import { useTheme } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { httpClient } from '../../common/httpclient';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../component/table/DefaultTable';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import Fade from '@mui/material/Fade';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import {
    AlertTriangle,
    ChevronDown,
    Download,
    ExternalLink,
    Eye,
    Image as ImageIcon,
    Info,
    Package,
    PackagePlus,
    Pencil,
    Search,
    Trash2,
    Upload,
    X,
} from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import StatusChip from '../../component/common/StatusChip';
import NewItemButton from '../../component/common/NewItemButton';
import FilterToolbar, { FilterChips } from '../../component/filter/FilterToolbar';
import DialogForm from '../../component/dialog/DialogForm';
import usePermissions from '../../hooks/usePermissions';
import useListFiltersSnapshot from '../../hooks/useListFiltersSnapshot';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import MobileProductsList from '../../component/product/MobileProductsList';
import BarcodeScannerDialog from '../../component/barcode/BarcodeScannerDialog';
import { protoIcon } from '../../component/common/ProtoIcon';

const UploadProtoIcon = protoIcon('upload');
const DownloadProtoIcon = protoIcon('download');
const RefreshProtoIcon = protoIcon('refresh');
const ScanProtoIcon = protoIcon('scan');
const EyeProtoIcon = protoIcon('eye');
const EditProtoIcon = protoIcon('edit');
const TrashProtoIcon = protoIcon('trash');
const MOBILE_PAGE_SIZE = 20;

const PAGE_SIZE_OPTIONS = [5, 8, 15, 25, 50];

// Snapshot dos filtros aplicados (sessionStorage, por empresa), gravado SOMENTE
// ao abrir a edição/reposição de um produto e restaurado só na volta (POP). Ver
// `hooks/useListFiltersSnapshot.js`. Removido também no logout (AccountMenu).
export const PRODUCTS_FILTERS_STORAGE_PREFIX = 'products_list_filters:';

const EMPTY_PRODUCTS_FILTERS = { category: '', subcategory: '', stock: 'with', search: '', page: 0, pageSize: 8 };

function sanitizeProductsFilters(saved) {
    return {
        category: typeof saved.category === 'string' ? saved.category : '',
        subcategory: typeof saved.subcategory === 'string' ? saved.subcategory : '',
        stock: saved.stock === 'without' ? 'without' : 'with',
        search: typeof saved.search === 'string' ? saved.search : '',
        page: Number.isInteger(saved.page) && saved.page >= 0 ? saved.page : 0,
        pageSize: PAGE_SIZE_OPTIONS.includes(saved.pageSize) ? saved.pageSize : 8,
    };
}

// Componentes auxiliares do drawer de visualização (somente leitura).
// `DetailSection` (icon-chip + título) segue o mesmo padrão de cabeçalho de
// seção usado no acordeão de `products/Form.js` (DESIGN-SYSTEM.md §5.5); as
// linhas de dado dentro de cada seção usam `PreviewRow`, o exato componente
// do card "Como aparece na venda" de `products/Form.js` — a prévia ao vivo
// estilo catálogo citada na tarefa (§5.6 + prototipo-telas-insercao.html,
// aba Novo Produto) — replicado aqui em modo leitura.
const DetailSection = ({ title, children }) => {
    return (
        <Box sx={{ mb: 2.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', mb: 1 }}>
                <Info size={15} strokeWidth={2} color="currentColor" />
                <Typography sx={{ fontSize: '11.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em', color: 'text.secondary' }}>
                    {title}
                </Typography>
            </Box>
            <Box
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: (t) => `${t.customRadius.hero}px`,
                    backgroundColor: 'background.paper',
                    p: '14px 18px',
                }}
            >
                {children}
            </Box>
        </Box>
    );
};

const PreviewRow = ({ label, value, highlight }) => {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: '12.5px', py: '7px', color: 'text.secondary' }}>
            <span>{label}</span>
            <Box component="b" sx={{ color: highlight ? 'success.main' : 'text.primary', textAlign: 'right', wordBreak: 'break-word' }}>
                {value || '—'}
            </Box>
        </Box>
    );
};

// Link do anúncio no Mercado Livre: vem de `marketplace_integrations[].url_product`
// (GET /stock/edit/{id}), o mesmo campo usado em `products/Form.js` ("Ver anúncio").
// Só aceita http(s) para não renderizar um href inesperado.
function getMercadoLivreUrl(product) {
    const integrations = Array.isArray(product?.marketplace_integrations)
        ? product.marketplace_integrations
        : [];
    const url = integrations.find(
        (integration) => integration
            && integration.marketplace === 'mercadolivre'
            && integration.url_product
    )?.url_product;
    return typeof url === 'string' && /^https?:\/\//i.test(url) ? url : '';
}

export default function List() {
    const theme = useTheme();
    const { initialFilters, saveSnapshot } = useListFiltersSnapshot(
        PRODUCTS_FILTERS_STORAGE_PREFIX,
        EMPTY_PRODUCTS_FILTERS,
        sanitizeProductsFilters,
    );
    const isMobileShell = useIsMobileShell();
    // Mobile: lista contínua ("Carregar mais") em vez de paginação; página própria para não
    // interferir na paginação do desktop/tablet.
    const [mobilePage, setMobilePage] = useState(0);
    // Mobile: chip "Todos" (sem filtro de estoque; a API aceita `stock` vazio).
    const [allStock, setAllStock] = useState(false);
    const [pickRestock, setPickRestock] = useState(false);
    const [scannerOpen, setScannerOpen] = useState(false);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(initialFilters.category);
    const [subcategories, setSubcategories] = useState([]);
    const [selectedSubcategory, setSelectedSubcategory] = useState(initialFilters.subcategory);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState(initialFilters.search);
    // Busca (nome ou código) com debounce — evita uma requisição por tecla.
    const [debouncedSearch, setDebouncedSearch] = useState(initialFilters.search.trim());
    const previousDebouncedSearch = useRef(initialFilters.search.trim());
    const [filteredDataWithStock, setFilteredDataWithStock] = useState(initialFilters.stock === 'with');
    // Paginação server-side (page é 0-based na tela; a API usa 1-based).
    const [products, setProducts] = useState([]);
    const [page, setPage] = useState(initialFilters.page);
    const [pageSize, setPageSize] = useState(initialFilters.pageSize);
    const [totalCount, setTotalCount] = useState(0);
    const [stockValue, setStockValue] = useState('0,00');
    const [reloadKey, setReloadKey] = useState(0);
    const navigate = useNavigate();
    const [openDialogView, setOpenDialogView] = useState(false);
    const [product, setProduct] = useState({});
    const [productLoading, setProductLoading] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);
    const [imageError, setImageError] = useState(false);
    const [openDialogConfirmDelete, setOpenDialogConfirmDelete] = useState(false);
    const [productToDelete, setProductToDelete] = useState({});
    const [anchorEl, setAnchorEl] = React.useState(null);
    const openMenu = Boolean(anchorEl);
    const [openDialogForm, setOpenDialogForm] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    // Mapa subcategory_id -> nome, usado só para exibir a coluna "Subcategoria"
    // da tabela (a lista completa vinda de GET /stock/all só traz IDs, não
    // nomes — ver `fetchAllSubcategoryNames`). Não é o mesmo estado do filtro
    // `subcategories` (que só carrega as subcategorias da categoria
    // selecionada no toolbar).
    const [subcategoryNameMap, setSubcategoryNameMap] = useState({});
    const [deleting, setDeleting] = useState(false);
    const { hasPermission } = usePermissions();

    const handleClickMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    // Resetar estado da imagem quando o modal for fechado
    useEffect(() => {
        if (!openDialogView) {
            setImageLoaded(false);
            setImageLoading(false);
            setImageError(false);
        }
    }, [openDialogView]);

    const fetchCategories = async () => {
        try {
            const response = await httpClient.get('/stock/categories');
            const responseData = response.data;

            setCategories(responseData);
            return responseData;
        } catch (error) {
            console.error('Error fetching categories:', error);
            return [];
        }
    };

    // Preenche `subcategoryNameMap` (subcategory_id -> nome) para a coluna
    // "Subcategoria" da tabela. GET /stock/all só traz IDs; sem `categoryId`,
    // GET /stock/subcategories retorna todas as subcategorias da empresa em
    // uma única requisição.
    const fetchAllSubcategoryNames = async () => {
        try {
            const response = await httpClient.get('/stock/subcategories');
            const map = {};
            (Array.isArray(response.data) ? response.data : []).forEach((sub) => {
                map[sub.id] = sub.name;
            });
            setSubcategoryNameMap(map);
        } catch (error) {
            console.error('Error fetching subcategory names for table display:', error);
        }
    };

    const fetchSubCategories = async (categoryId) => {
        try {
            if (!categoryId) {
                setSubcategories([]);
                return;
            }
            const response = await httpClient.get(`/stock/subcategories?categoryId=${categoryId}`);
            const responseData = response.data;
            setSubcategories(responseData);
        } catch (error) {
            console.error('Error fetching subcategories:', error);
            setSubcategories([]);
        }
    };

    // Debounce da busca; ao mudar o termo volta para a primeira página.
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
        return () => clearTimeout(timer);
    }, [search]);

    // Só volta para a primeira página se o termo realmente mudou (na montagem
    // o termo restaurado não deve zerar a página restaurada).
    useEffect(() => {
        if (previousDebouncedSearch.current === debouncedSearch) return;
        previousDebouncedSearch.current = debouncedSearch;
        setPage(0);
        setMobilePage(0);
    }, [debouncedSearch]);

    // Busca a página atual no servidor sempre que filtros/página/tamanho mudam.
    useEffect(() => {
        let cancelled = false;
        const requestPage = isMobileShell ? mobilePage : page;
        const params = {
            page: requestPage + 1,
            page_size: isMobileShell ? MOBILE_PAGE_SIZE : pageSize,
        };
        if (!(isMobileShell && allStock)) params.stock = filteredDataWithStock ? 'with' : 'without';
        if (selectedCategory) params.categoryId = selectedCategory;
        if (selectedSubcategory) params.subcategoryId = selectedSubcategory;
        if (debouncedSearch) params.search = debouncedSearch;

        setLoading(true);
        httpClient.get('/stock/all', { params })
            .then((response) => {
                if (cancelled) return;
                if (response.status === 403) {
                    navigate('/');
                    return;
                }
                const rows = response.data.items || [];
                const total = response.data.total || 0;
                // Página ficou além do fim (ex.: excluiu o último item da última página).
                if (!isMobileShell && rows.length === 0 && total > 0 && page > 0) {
                    setPage(Math.ceil(total / pageSize) - 1);
                    return;
                }
                if (isMobileShell && requestPage > 0) {
                    setProducts((prev) => [...prev, ...rows.filter((row) => !prev.some((p) => p.id === row.id))]);
                } else {
                    setProducts(rows);
                }
                setTotalCount(total);
                setStockValue(response.data.stock_value || '0,00');
            })
            .catch((error) => {
                if (cancelled) return;
                console.error('Error fetching data:', error);
                setProducts([]);
                setTotalCount(0);
                setStockValue('0,00');
                setSnackbarMessage('Erro ao buscar produtos');
                setSnackbarSeverity('error');
                setOpenSnackbar(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, selectedCategory, selectedSubcategory, filteredDataWithStock, debouncedSearch, reloadKey, isMobileShell, mobilePage, allStock]);

    // Recarrega a página atual (excluir muda contagem e valor em estoque).
    const reloadCurrentPage = () => {
        setMobilePage(0);
        setReloadKey((k) => k + 1);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPage(0);
    };

    // Grava os filtros aplicados imediatamente antes de ir à edição/reposição; o
    // state `fromList` faz o "Fechar" do formulário usar o histórico (POP) e
    // restaurá-los.
    const goToProductPage = (path) => {
        saveSnapshot({
            category: selectedCategory,
            subcategory: selectedSubcategory,
            stock: filteredDataWithStock ? 'with' : 'without',
            search: debouncedSearch,
            page,
            pageSize,
        });
        navigate(path, { state: { fromList: true } });
    };

    const fetchProduct = async (id) => {
        try {
            setProductLoading(true);
            setImageLoaded(false);
            const response = await httpClient.get(`/stock/edit/${id}`);
            const responseData = response.data;
            setProduct(responseData);

            // Carregar a imagem do produto após obter os dados
            await loadProductImage(responseData);
        } catch (error) {
            console.error('Error fetching product:', error);
            setImageLoading(false);
            setImageError(true);
            setImageLoaded(true);
        } finally {
            setProductLoading(false);
        }
    }

    const handleDeleteProduct = async (id) => {
        setDeleting(true);
        try {
            const response = await httpClient.delete(`/stock/${id}`);
            const responseData = response.data;
            setOpenDialogConfirmDelete(false);
            reloadCurrentPage();
            setSnackbarMessage('Produto excluído com sucesso');
            setSnackbarSeverity('success');
            setOpenSnackbar(true);
            return responseData;
        } catch (error) {
            console.error('Error deleting product:', error);
            setSnackbarMessage('Erro ao excluir produto');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setDeleting(false);
        }
    }

    const handleExport = async () => {
        try {
            await httpClient.downloadFile('/stock/export', {
                title: 'produtos',
                extension: 'csv',
                responseType: 'blob'
            });
        } catch (error) {
            console.error('Error exporting products:', error);
            setSnackbarMessage('Erro ao exportar produtos');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        }
    }

    const handleChangeCategory = (category) => {
        setSelectedCategory(category);
        setSelectedSubcategory(''); // Reset subcategoria quando categoria muda
        fetchSubCategories(category); // Buscar subcategorias da categoria selecionada
        setPage(0);
        setMobilePage(0);
    };

    const handleChangeSubcategory = (subcategory) => {
        setSelectedSubcategory(subcategory);
        setPage(0);
        setMobilePage(0);
    };

    const handleChangeSearch = (searchValue) => {
        setSearch(searchValue);
    };

    // "Buscar": aplica o termo na hora (sem esperar o debounce) e volta à página 1.
    const handleApplySearch = () => {
        setDebouncedSearch(search.trim());
        setPage(0);
        setMobilePage(0);
    };

    const handleWithStock = (event) => {
        setFilteredDataWithStock(event);
        setPage(0);
        setMobilePage(0);
    }

    // "Limpar" do report-toolbar (DESIGN-SYSTEM.md §5.5 "tb-field") — reseta
    // os 4 filtros ao estado inicial da tela (categoria/subcategoria/busca
    // vazios, "somente com estoque" ligado, mesmo default de `useState`).
    const handleClearFilters = () => {
        setSelectedCategory('');
        setSelectedSubcategory('');
        setSubcategories([]);
        setSearch('');
        setDebouncedSearch('');
        setFilteredDataWithStock(true);
        setAllStock(false);
        setPage(0);
        setMobilePage(0);
    };

    // Mobile: chips de estoque (Todos / Com estoque / Sem estoque) aplicam na hora.
    const handleMobileStockMode = (mode) => {
        setAllStock(mode === 'all');
        if (mode !== 'all') setFilteredDataWithStock(mode === 'with');
        setMobilePage(0);
    };

    // Mobile: folha de filtros aplica categoria + subcategoria juntas.
    const handleMobileApplyFilters = (categoryId, subcategoryId) => {
        if (categoryId !== selectedCategory) {
            handleChangeCategory(categoryId);
        }
        setSelectedSubcategory(subcategoryId);
        setMobilePage(0);
    };

    // Mobile: lê um código de barras e usa como termo de busca.
    const handleBarcodeDetected = (code) => {
        setSearch(code);
        setDebouncedSearch(String(code).trim());
        setPage(0);
        setMobilePage(0);
    };

    const handleOpenDialogView = (id) => {
        setOpenDialogView(true);
        setProduct({});
        setProductLoading(true);
        setImageLoaded(false);
        setImageLoading(true);
        setImageError(false);
        fetchProduct(id);
    }

    // Função para carregar a imagem do produto
    const loadProductImage = async (productData) => {
        if (!productData || !productData.image) {
            console.log('Produto sem imagem:', productData);
            setImageLoading(false);
            setImageError(false);
            setImageLoaded(true);
            return;
        }

        try {
            const imageUrl = getImageUrl(productData.image);
            console.log('Tentando carregar imagem:', imageUrl);
            await loadImageWithTimeout(imageUrl, 10000);
            console.log('Imagem carregada com sucesso');
            setImageLoading(false);
            setImageError(false);
            setImageLoaded(true);
        } catch (error) {
            console.error('Erro ao carregar imagem:', error);
            setImageLoading(false);
            setImageError(true);
            setImageLoaded(true);
        }
    };

    const handleImageLoad = () => {
        setImageLoading(false);
        setImageError(false);
        setImageLoaded(true);
    };

    const handleImageError = () => {
        setImageLoading(false);
        setImageError(true);
        setImageLoaded(true);
    };

    // Função para construir a URL da imagem
    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;

        // Se já é uma URL completa, retorna como está
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
            return imagePath;
        }

        // Se é um blob URL, retorna como está
        if (imagePath.startsWith('blob:')) {
            return imagePath;
        }

        // Se é um caminho relativo, adiciona o prefixo da API
        const baseUrl = process.env.REACT_APP_API_URL || '';
        const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
        return `${baseUrl}${cleanPath}`;
    };

    // Função para carregar imagem com timeout
    const loadImageWithTimeout = (imageUrl, timeout = 10000) => {
        return new Promise((resolve, reject) => {
            if (!imageUrl) {
                reject(new Error('URL da imagem não fornecida'));
                return;
            }

            const img = new Image();
            const timer = setTimeout(() => {
                img.src = '';
                reject(new Error('Timeout ao carregar imagem'));
            }, timeout);

            img.onload = () => {
                clearTimeout(timer);
                resolve(img);
            };

            img.onerror = () => {
                clearTimeout(timer);
                reject(new Error('Erro ao carregar imagem'));
            };

            img.src = imageUrl;
        });
    };

    const actions = [
        {
            icon: <Eye size={18} strokeWidth={2} />,
            props: {
                onClick: (row) => {
                    handleOpenDialogView(row.id);
                },
                tooltip: 'Visualizar',
                cursor: 'pointer',
                sx: { mr: 1 }
            }

        },
        ...(hasPermission('product.update') ? [{
            icon: <Pencil size={18} strokeWidth={2} />,
            props: {
                onClick: (row) => {
                    goToProductPage(`/products/edit/${row.id}`);
                },
                tooltip: 'Editar',
                style: { cursor: 'pointer' }

            }
        }] : []),
        ...(hasPermission('product.restock') ? [{
            // Produto genérico não tem controle de estoque: esconde "Repor estoque".
            filterRow: (row) => !row.is_generic,
            icon: <PackagePlus size={18} strokeWidth={2} />,
            props: {
                onClick: (row) => {
                    goToProductPage(`/products/${row.id}/reposition`);
                },
                tooltip: 'Repor estoque',
                style: { cursor: 'pointer' }
            }
        }] : []),
        ...(hasPermission('product.delete') ? [{
            icon: <Trash2 size={18} strokeWidth={2} color={theme.palette.error.main} />,
            props: {
                onClick: (row) => {
                    setProductToDelete(row.id);
                    setOpenDialogConfirmDelete(true);
                },
                tooltip: 'Apagar',
                style: { cursor: 'pointer' }
            }
        }] : []),
    ]

    // Mobile: ações do produto na folha aberta ao tocar na linha (mesmas permissões do desktop).
    const mobileRowActions = (row) => [
        { label: 'Visualizar', icon: EyeProtoIcon, onClick: () => handleOpenDialogView(row.id) },
        ...(hasPermission('product.update') ? [{ label: 'Editar', icon: EditProtoIcon, onClick: () => goToProductPage(`/products/edit/${row.id}`) }] : []),
        ...(hasPermission('product.restock') && !row.is_generic ? [{ label: 'Repor estoque', icon: RefreshProtoIcon, onClick: () => goToProductPage(`/products/${row.id}/reposition`) }] : []),
        ...(hasPermission('product.delete') ? [{
            label: 'Excluir',
            icon: TrashProtoIcon,
            destructive: true,
            onClick: () => {
                setProductToDelete(row.id);
                setOpenDialogConfirmDelete(true);
            },
        }] : []),
    ];

    // Mobile: menu "Mais" da barra superior (folha).
    const mobileMoreItems = [
        ...(hasPermission('product.import') ? [{ label: 'Importar planilha', icon: UploadProtoIcon, onClick: () => navigate('/products/import') }] : []),
        ...(hasPermission('product.restock') ? [{ label: 'Repor estoque', icon: RefreshProtoIcon, onClick: () => setPickRestock(true) }] : []),
        { label: 'Ler código de barras', icon: ScanProtoIcon, onClick: () => setScannerOpen(true) },
        ...(hasPermission('product.export') ? [{ label: 'Exportar', icon: DownloadProtoIcon, onClick: handleExport }] : []),
    ];

    // Colunas do template de lista (§6.1) — "produto com thumb+SKU, categoria,
    // subcategoria, preço, estoque com pill de status, ações", igual à tabela
    // de `prototipo-lista-produtos.html`. A coluna "Status" (ativo/inativo) do
    // protótipo foi propositalmente deixada de fora desta implementação: ver
    // nota grande logo abaixo do componente sobre a lacuna de dado no backend.
    const columns = [
        {
            path: 'name',
            name: 'Produto',
            invisible: false,
            render: (row) => (
                <Box sx={{ display: 'flex', justifyContent: 'center', textAlign: 'center' }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary', whiteSpace: 'normal', lineHeight: 1.3 }}>
                            {row.name}
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '2px' }}>
                            Código {row.code}
                        </Typography>
                    </Box>
                </Box>
            ),
        },
        {
            path: 'category_id',
            name: 'Categoria',
            invisible: false,
            render: (row) => {
                const name = categories.find((c) => String(c.id) === String(row.category_id))?.name;
                return (
                    <Typography component="span" sx={{ fontSize: '13px', color: 'text.secondary' }}>
                        {name || '—'}
                    </Typography>
                );
            }
        },
        {
            path: 'subcategory_id',
            name: 'Subcategoria',
            invisible: false,
            render: (row) => {
                const name = subcategoryNameMap[row.subcategory_id];
                return (
                    <Typography component="span" sx={{ fontSize: '13px', color: 'text.secondary' }}>
                        {name || '—'}
                    </Typography>
                );
            }
        },
        {
            path: 'price',
            name: 'Preço',
            invisible: false,
            render: (row) => {
                return <Typography component="span" sx={{ fontSize: '13px', fontWeight: 700, color: 'success.main' }}>
                    R$ {row.price}
                </Typography>
            }
        },
        {
            path: 'quantity',
            name: 'Estoque',
            invisible: false,
            render: (row) => (
                <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>
                        {row.quantity} un.
                    </Typography>
                    <StatusChip domain="stockLevel" value={row.quantity > 0 ? 'em estoque' : 'sem estoque'} />
                </Box>
            ),
        },
    ];

    useEffect(() => {
        fetchCategories();
        fetchAllSubcategoryNames();
        // Restaurou uma categoria do snapshot: carrega as subcategorias dela.
        if (initialFilters.category) fetchSubCategories(initialFilters.category);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectedCategoryName = categories.find(c => c.id === selectedCategory)?.name;
    const selectedSubcategoryName = subcategories.find(s => s.id === selectedSubcategory)?.name;

    // Micro-label fixo do `tb-field` (DESIGN-SYSTEM.md §5.5/§9), igual a
    // `pages/services/List.js`/`pages/sales/List.js`/`pages/customers/List.js`.
    const TbLabel = ({ children }) => (
        <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
            {children}
        </Typography>
    );

    const SelectChevron = (iconProps) => <ChevronDown size={14} strokeWidth={2} {...iconProps} />;

    return (
        <PageScaffold
            links={[]}
            titlePageTarget={'Produtos'}
            allowBleed={isMobileShell}
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={snackbarMessage}
            severity={snackbarSeverity}
        >
            {isMobileShell ? (
                <MobileProductsList
                    products={products}
                    loading={loading}
                    totalCount={totalCount}
                    hasMore={products.length < totalCount}
                    onLoadMore={() => setMobilePage((current) => current + 1)}
                    stockMode={allStock ? 'all' : (filteredDataWithStock ? 'with' : 'without')}
                    onStockModeChange={handleMobileStockMode}
                    search={search}
                    onSearchChange={handleChangeSearch}
                    categories={categories}
                    subcategories={subcategories}
                    category={selectedCategory}
                    subcategory={selectedSubcategory}
                    onCategoryDraftChange={fetchSubCategories}
                    onApplyFilters={handleMobileApplyFilters}
                    onClearFilters={handleClearFilters}
                    canCreate={hasPermission('product.create')}
                    onNew={() => navigate('/products/new')}
                    moreItems={mobileMoreItems}
                    rowActions={mobileRowActions}
                    pickMode={pickRestock}
                    onPick={(row) => {
                        // Produto genérico não pode ser reposto: ignora o toque e mantém o modo de seleção.
                        if (row.is_generic) return;
                        setPickRestock(false);
                        goToProductPage(`/products/${row.id}/reposition`);
                    }}
                    onCancelPick={() => setPickRestock(false)}
                />
            ) : (
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>

                {/* Page head — título + descrição + ação primária (padrão das telas de lista:
                    Vendas/Clientes em prototipo-novo-layout-erp.html — sem ícone-badge, esse
                    é o padrão do painel inicial/hero, não do template de lista §6.1) */}
                <Box sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'flex-end' },
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 3,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '11px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            background: (t) => t.palette.gradient.brand,
                        }}>
                            <Package size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Produtos
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Consulte, edite e organize o catálogo de produtos.
                            </Typography>
                        </Box>
                    </Box>

                    <Stack direction="row" spacing={1}>
                        {hasPermission('product.export') && (
                            <Tooltip title="Exportar produtos" placement="top" arrow>
                                <Button
                                    variant="outlined"
                                    startIcon={<Download size={15} strokeWidth={2} />}
                                    onClick={handleExport}
                                >
                                    Exportar
                                </Button>
                            </Tooltip>
                        )}
                        {hasPermission('product.import') && (
                            <Tooltip title="Importar produtos" placement="top" arrow>
                                <Button
                                    variant="outlined"
                                    startIcon={<Upload size={15} strokeWidth={2} />}
                                    onClick={() => navigate('/products/import')}
                                >
                                    Importar
                                </Button>
                            </Tooltip>
                        )}
                        {hasPermission('product.create') && (
                            <NewItemButton label="Novo produto" onClick={() => navigate('/products/new')} />
                        )}
                    </Stack>
                </Box>

                {/* report-toolbar — Categoria/Subcategoria (dependente)/Buscar por
                    nome ou SKU/toggle "Somente com estoque", igual a
                    `prototipo-lista-produtos.html` e ao mesmo padrão de
                    `pages/services/List.js`. */}
                <FilterToolbar
                    plain
                    primaryIndex={2}
                    activeCount={(selectedCategory ? 1 : 0) + (selectedSubcategory ? 1 : 0)}
                    onClear={handleClearFilters}
                    onApply={handleApplySearch}
                    primary={(
                        <Box sx={{ flex: '0 0 240px' }}>
                            <TbLabel>Buscar</TbLabel>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Nome ou Código do produto…"
                                value={search}
                                onChange={(e) => handleChangeSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search size={15} strokeWidth={2} />
                                        </InputAdornment>
                                    ),
                                    sx: { height: 40, fontSize: '13px', borderRadius: '9px' },
                                }}
                            />
                        </Box>
                    )}
                    actions={(
                        <Stack direction="row" spacing={1.25} alignItems="center">
                            <Button
                                variant="text"
                                onClick={handleClearFilters}
                                sx={{ height: 40, px: 1.25, color: 'text.secondary', fontSize: '12.5px', fontWeight: 600, '&:hover': { color: 'primary.main', bgcolor: 'transparent', textDecoration: 'underline' } }}
                            >
                                Limpar
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<Search size={15} strokeWidth={2} />}
                                onClick={handleApplySearch}
                                sx={{ height: 40, px: 2.25 }}
                            >
                                Buscar
                            </Button>
                        </Stack>
                    )}
                >
                        <Box>
                            <TbLabel>Categoria</TbLabel>
                            <FormControl size="small" sx={{ minWidth: 170 }}>
                                <Select
                                    value={selectedCategory}
                                    displayEmpty
                                    IconComponent={SelectChevron}
                                    onChange={(e) => handleChangeCategory(e.target.value)}
                                    renderValue={(value) => (value ? (selectedCategoryName || '') : 'Todas')}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: '9px' }}
                                >
                                    <MenuItem value="">Todas</MenuItem>
                                    {categories.map((category) => (
                                        <MenuItem key={category.id} value={category.id}>
                                            {category.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box>
                            <TbLabel>Subcategoria</TbLabel>
                            <FormControl size="small" sx={{ minWidth: 170 }} disabled={!selectedCategory}>
                                <Select
                                    value={selectedSubcategory}
                                    displayEmpty
                                    IconComponent={SelectChevron}
                                    onChange={(e) => handleChangeSubcategory(e.target.value)}
                                    renderValue={(value) => (value ? (selectedSubcategoryName || '') : (selectedCategory ? 'Todas' : 'Selecione uma categoria'))}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: '9px' }}
                                >
                                    <MenuItem value="">
                                        {selectedCategory ? 'Todas' : 'Selecione uma categoria'}
                                    </MenuItem>
                                    {subcategories.map((subcategory) => (
                                        <MenuItem key={subcategory.id} value={subcategory.id}>
                                            {subcategory.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box>
                            <TbLabel>Estoque</TbLabel>
                            <FormControl size="small" sx={{ minWidth: 170 }}>
                                <Select
                                    value={filteredDataWithStock ? 'with' : 'without'}
                                    IconComponent={SelectChevron}
                                    onChange={(e) => {
                                        const withStock = e.target.value === 'with';
                                        handleWithStock(withStock);
                                    }}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: '9px' }}
                                >
                                    <MenuItem value="with">Somente com estoque</MenuItem>
                                    <MenuItem value="without">Somente sem estoque</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                </FilterToolbar>

                {/* chips-row — filtros ativos + contagem/valor em estoque. */}
                <FilterChips>
                    {selectedCategory && (
                        <Chip
                            label={`Categoria: ${selectedCategoryName || ''}`}
                            size="small"
                            onDelete={() => handleChangeCategory('')}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                        />
                    )}
                    {selectedSubcategory && (
                        <Chip
                            label={`Subcategoria: ${selectedSubcategoryName || ''}`}
                            size="small"
                            onDelete={() => handleChangeSubcategory('')}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    {search && (
                        <Chip
                            label={`Busca: "${search}"`}
                            size="small"
                            onDelete={() => handleChangeSearch('')}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    {/* Sem `onDelete`: ao contrário de categoria/subcategoria/busca, o
                        toggle "Somente com estoque" não tem um estado neutro "sem
                        filtro" (`handleWithStock` sempre filtra por >0 ou <=0) — o
                        chip só reflete o estado atual do switch acima. */}
                    <Chip
                        label={filteredDataWithStock ? 'Somente com estoque' : 'Somente sem estoque'}
                        size="small"
                        sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                    />
                    <Chip
                        label={`${totalCount} produto${totalCount === 1 ? '' : 's'} encontrado${totalCount === 1 ? '' : 's'}`}
                        size="small"
                        sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                    />
                    <Chip
                        label={`Valor em estoque: R$ ${stockValue}`}
                        size="small"
                        sx={{ bgcolor: 'success.bg', color: 'success.main' }}
                    />
                </FilterChips>

                <DefaultTable
                    colunms={columns}
                    data={products}
                    iconsActions={actions}
                    loading={loading}
                    onRowClick={(row) => handleOpenDialogView(row.id)}
                    rowPerPage={pageSize}
                    rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                    itemLabel="produtos"
                    serverPagination={{
                        page,
                        rowsPerPage: pageSize,
                        total: totalCount,
                        onPageChange: setPage,
                        onRowsPerPageChange: handlePageSizeChange,
                    }}
                />
            </Box>
            )}

            {/* Drawer de Visualização de Produto — somente leitura.
                Ref: DESIGN-SYSTEM.md §5.6 "Side drawer" (slide da direita,
                largura fixa, dispensa por clique no scrim ou no X) +
                prototipo-telas-insercao.html (aba Novo Produto, prévia ao
                vivo estilo catálogo em `products/Form.js`), aqui alimentada
                pelo produto carregado em vez do formulário. */}
            <Drawer
                anchor={isMobileShell ? 'bottom' : 'right'}
                open={openDialogView}
                onClose={() => setOpenDialogView(false)}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 560, md: 640 },
                        maxWidth: '100vw',
                        display: 'flex',
                        flexDirection: 'column',
                        ...(isMobileShell ? { maxHeight: '88%', width: '100%', borderRadius: '24px 24px 0 0', backgroundImage: 'none' } : {}),
                    },
                }}
            >
                {/* Header — título + subtítulo + X, igual ao `.drawer-head` do
                    guia visual (design-system.html), sem gradiente: gradiente
                    é reservado para modal central/cabeçalhos de página cheia. */}
                <Box sx={{ p: '20px 22px', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, flexShrink: 0 }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 800, mb: '4px' }} noWrap>
                            {product.name || 'Detalhes do produto'}
                        </Typography>
                        <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                            {product.code ? `Código ${product.code}` : 'Prévia somente leitura'}
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setOpenDialogView(false)} size="small" aria-label="Fechar">
                        <X size={18} strokeWidth={2} />
                    </IconButton>
                </Box>

                <Box sx={{ p: '20px 22px', flex: 1, overflowY: 'auto' }}>
                    {productLoading ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: 300,
                            flexDirection: 'column'
                        }}>
                            <CircularProgress size={44} thickness={4} />
                            <Typography
                                variant="body2"
                                sx={{ mt: 1.5, color: 'text.secondary' }}
                            >
                                Carregando informações do produto...
                            </Typography>
                        </Box>
                    ) : (
                        <Fade in={!productLoading} timeout={400}>
                            <Box>
                                {/* Card de prévia ao vivo estilo catálogo — mesmo
                                    componente visual de `products/Form.js`
                                    ("Como aparece na venda"), em modo leitura. */}
                                <Box
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: (t) => `${t.customRadius.hero}px`,
                                        backgroundColor: 'background.paper',
                                        p: 2,
                                        mb: 2.25,
                                    }}
                                >
                                    <Box sx={{
                                        height: 220,
                                        borderRadius: '12px',
                                        mb: 1.5,
                                        overflow: 'hidden',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative',
                                        backgroundColor: 'info.bg',
                                    }}>
                                        {imageLoading && (
                                            <Box sx={{
                                                position: 'absolute',
                                                inset: 0,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: 'background.paper',
                                                zIndex: 1
                                            }}>
                                                <CircularProgress size={36} thickness={4} sx={{ mb: 1 }} />
                                                <Typography variant="caption" color="text.secondary">
                                                    Carregando imagem...
                                                </Typography>
                                            </Box>
                                        )}

                                        {imageError || !product.image ? (
                                            <Box sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                height: '100%',
                                                color: imageError ? 'text.secondary' : 'primary.main'
                                            }}>
                                                {imageError ? (
                                                    <>
                                                        <ImageIcon size={40} strokeWidth={1.5} style={{ opacity: 0.5, marginBottom: 8 }} />
                                                        <Typography variant="caption" sx={{ textAlign: 'center' }}>
                                                            Erro ao carregar imagem
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ textAlign: 'center', mt: 0.5, opacity: 0.7 }}>
                                                            Verifique a conexão ou tente novamente
                                                        </Typography>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => loadProductImage(product)}
                                                            sx={{ mt: 1 }}
                                                        >
                                                            Tentar novamente
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <Package size={40} strokeWidth={1.5} />
                                                )}
                                            </Box>
                                        ) : (
                                            <img
                                                src={getImageUrl(product.image)}
                                                alt={product.name}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'contain',
                                                    opacity: imageLoading ? 0 : 1,
                                                    transition: 'opacity 0.3s ease'
                                                }}
                                                onLoad={handleImageLoad}
                                                onError={handleImageError}
                                            />
                                        )}
                                    </Box>

                                    <Typography sx={{ fontSize: 14, fontWeight: 800, mb: 0.75, minHeight: 36 }}>
                                        {product.name || 'Nome do produto'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
                                        <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'primary.main' }}>
                                            {product.sale_price ? `R$ ${product.sale_price}` : 'R$ 0,00'}
                                        </Typography>
                                        <Box
                                            component="span"
                                            sx={{
                                                fontSize: 11,
                                                fontWeight: 700,
                                                px: '9px',
                                                py: '3px',
                                                borderRadius: '20px',
                                                backgroundColor: product.quantity > 0 ? 'info.bg' : 'error.bg',
                                                color: product.quantity > 0 ? 'primary.main' : 'error.main',
                                            }}
                                        >
                                            estq {product.quantity ?? 0}
                                        </Box>
                                    </Box>
                                    <Typography sx={{ fontSize: 11, color: 'text.disabled', mb: 0.5 }}>
                                        Margem de lucro: <Box component="b" sx={{ color: 'text.primary' }}>
                                            {product.profit_margin !== undefined && product.profit_margin !== null ? `${product.profit_margin}%` : '—'}
                                        </Box>
                                    </Typography>
                                    <Box sx={{ height: 8, borderRadius: '6px', backgroundColor: 'background.subtle', overflow: 'hidden' }}>
                                        <Box
                                            sx={{
                                                height: '100%',
                                                width: `${Math.max(0, Math.min(100, Number(product.profit_margin) || 0))}%`,
                                                borderRadius: '6px',
                                                background: (t) => `linear-gradient(90deg, ${t.palette.brand2.main}, ${t.palette.success.main})`,
                                                transition: 'width .3s ease',
                                            }}
                                        />
                                    </Box>

                                    {product.profit_margin !== undefined && product.profit_margin !== null
                                        && product.expected_profit_margin !== undefined && product.expected_profit_margin !== null
                                        && product.profit_margin < product.expected_profit_margin && (
                                        <Alert
                                            severity="warning"
                                            icon={<AlertTriangle size={18} strokeWidth={2} />}
                                            sx={{ mt: 1.5 }}
                                        >
                                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                                                Margem abaixo do esperado
                                            </Typography>
                                            <Typography variant="caption" component="div">
                                                Atual ({product.profit_margin}%) está abaixo da margem esperada ({product.expected_profit_margin}%).
                                            </Typography>
                                        </Alert>
                                    )}
                                </Box>

                                {/* Dados cadastrais */}
                                <DetailSection title="Dados cadastrais">
                                    <PreviewRow label="Código" value={product.code} />
                                    <PreviewRow label="Categoria" value={product.category_name} />
                                    <PreviewRow label="Subcategoria" value={product.subcategory_name} />
                                    <PreviewRow label="Fornecedor" value={product.supplier_name} />
                                    <PreviewRow label="Tipo" value={product.is_generic ? 'Genérico' : 'Padrão'} />
                                </DetailSection>

                                {/* Preço, margem e estoque atual */}
                                <DetailSection title="Preço, margem e estoque">
                                    <PreviewRow label="Preço de venda" value={product.sale_price ? `R$ ${product.sale_price}` : null} highlight />
                                    <PreviewRow label="Custo" value={product.cost ? `R$ ${product.cost}` : null} />
                                    <PreviewRow
                                        label="Margem de lucro"
                                        value={product.profit_margin !== undefined && product.profit_margin !== null ? `${product.profit_margin}%` : null}
                                    />
                                    <PreviewRow label="Quantidade em estoque" value={product.quantity !== undefined && product.quantity !== null ? `${product.quantity} unidades` : null} />
                                    <PreviewRow label="Quantidade mínima" value={product.min_quantity !== undefined && product.min_quantity !== null ? `${product.min_quantity} unidades` : null} />
                                    <PreviewRow label="Quantidade vendida" value={product.quantity_sold !== undefined && product.quantity_sold !== null ? `${product.quantity_sold} unidades` : '0 unidades'} />
                                </DetailSection>

                                {/* Anúncio no Mercado Livre — só quando o produto já tem link */}
                                {getMercadoLivreUrl(product) && (
                                    <DetailSection title="Marketplace">
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, fontSize: '12.5px', py: '7px', color: 'text.secondary' }}>
                                            <span>Mercado Livre</span>
                                            <Box
                                                component="a"
                                                href={getMercadoLivreUrl(product)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '5px',
                                                    fontWeight: 700,
                                                    color: 'primary.main',
                                                    textDecoration: 'none',
                                                    '&:hover': { textDecoration: 'underline' },
                                                }}
                                            >
                                                Ver anúncio
                                                <ExternalLink size={14} strokeWidth={2} />
                                            </Box>
                                        </Box>
                                    </DetailSection>
                                )}
                            </Box>
                        </Fade>
                    )}
                </Box>

                {/* Footer sticky com ações — Fechar/Editar */}
                <Box sx={{
                    p: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1.5,
                    flexShrink: 0,
                }}>
                    <Button
                        variant="outlined"
                        onClick={() => setOpenDialogView(false)}
                        size="small"
                    >
                        Fechar
                    </Button>
                    {hasPermission('product.update') && (
                        <Button
                            variant="contained"
                            onClick={() => {
                                setOpenDialogView(false);
                                goToProductPage(`/products/edit/${product.id}`);
                            }}
                            size="small"
                        >
                            Editar
                        </Button>
                    )}
                </Box>
            </Drawer>
            <BarcodeScannerDialog
                open={scannerOpen}
                onClose={() => setScannerOpen(false)}
                onDetected={handleBarcodeDetected}
            />
            <DialogForm
                iconType={'warning'}
                open={openDialogConfirmDelete}
                title={'Atenção'}
                confirmButtonText={'Sim, Excluir'}
                content={'Tem certeza que deseja excluir este produto?'}
                secondaryContent={'Esta ação não pode ser desfeita. O produto será removido permanentemente do sistema.'}
                onSubmit={() => {
                    handleDeleteProduct(productToDelete);
                }}
                onClose={() => {
                    setOpenDialogConfirmDelete(false);
                }}
                loading={deleting}
                loadingText="Removendo..."
            />
        </PageScaffold>
    );
}
